import React, { useContext, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import { device, LAYOUT_CONSTANTS } from "../lib/breakpoints";
import InviteStatsCard from "../components/invite/InviteStatsCard";
import { message, Modal, Input } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import PageHeader from "../components/PageHeader";

const Offers = () => {
  const { user, userInfo } = useContext(AuthContext);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [shuffledTemplates, setShuffledTemplates] = useState([]);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);

  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      message.success("Copied to clipboard");
    } catch (err) {
      console.error("Clipboard copy failed:", err);
      message.error("Could not copy. Please copy manually.");
    }
  };

  const handleCopyEmail = () => {
    copyToClipboard("contact@clumse.com");
  };

  const EMAIL_TEMPLATES = [
    `Subject: Amazing tool I found for our REIA members!\n\nHi [Local REIA Name],\n\nI had to share this with you, I've been using Clumse for my rentals and honestly, it's been a game changer. Clumse AI answers tenant questions around the clock, and I don't have to chase down rent payments anymore. The whole thing just works.\n\nI really think our members would love this. It's saved me time and feels like an AI property manager without typical PM fees.\n\nHope you're doing well!\n[Your Name]`,
    `Subject: You have to check this out - Clumse\n\nHello [Local REIA Name],\n\nI'm so excited about this property management tool I found called Clumse. I've been using it for a few months now and I can't believe how much easier my life has become. The AI actually understands what my tenants are asking and gives them real answers!\n\nI thought our REIA members would really benefit from knowing about this. It's honestly the best thing I've discovered for my rental business.\n\nThanks for everything you do!\n[Your Name]`,
    `Subject: Found something incredible for our group\n\nHi [Local REIA Name],\n\nI just had to tell you about Clumse - I'm absolutely loving it! It's like having a property management assistant that never sleeps. My tenants can get help anytime, rent gets collected automatically, and I actually have time to focus on growing my portfolio.\n\nI really think our members would get a lot out of this. It's been such a relief to have everything organized in one place.\n\nBest,\n[Your Name]`,
    `Subject: This tool changed everything for my rentals\n\nHi [Local REIA Name],\n\nI found this amazing platform called Clumse and I had to share it with you. It's completely transformed how I manage my properties. The AI assistant is so smart - it handles all the routine questions from tenants, and I don't have to worry about missing rent payments anymore.\n\nI think our REIA members would really appreciate knowing about this. It's made my life so much easier!\n\nHope you're having a great week!\n[Your Name]`,
    `Subject: Can't stop talking about this - Clumse\n\nHello [Local REIA Name],\n\nI'm probably annoying everyone by talking about Clumse so much, but I just had to share it with you! I've been using it for my rentals and it's incredible. The AI actually sounds human when it talks to my tenants, and everything just runs so smoothly now.\n\nI really think our members would love this. It's like having a super smart assistant that never takes a day off.\n\nThanks for all you do for our group!\n[Your Name]`,
    `Subject: This is going to blow your mind - Clumse\n\nHi [Local REIA Name],\n\nI found something that's completely changed how I think about property management. Clumse has this AI assistant that's so good, my tenants think they're talking to a real person! Plus, rent collection is automatic and I get these beautiful reports that actually make sense.\n\nI had to share this with you because I know our members would absolutely love it. It's been such a game changer for me.\n\nHope you're doing well!\n[Your Name]`,
    `Subject: You won't believe what I found - Clumse\n\nHi [Local REIA Name],\n\nI'm so excited about this property management tool I discovered called Clumse. It's like having a genius assistant that handles everything - tenant questions, rent collection, maintenance requests. I actually have time to focus on finding new properties now!\n\nI really think our REIA members would benefit from knowing about this. It's honestly the best investment I've made for my rental business.\n\nThanks for everything!\n[Your Name]`,
    `Subject: This tool is incredible - had to share!\n\nHello [Local REIA Name],\n\nI've been using Clumse for my rentals and I'm honestly amazed at how well it works. The AI assistant is so smart and helpful, and my tenants love being able to get instant answers to their questions. Plus, I don't have to chase down rent payments anymore!\n\nI thought our members would really appreciate knowing about this. It's made property management so much easier and more enjoyable.\n\nHope you're having a great day!\n[Your Name]`,
    `Subject: Found the holy grail of property management\n\nHi [Local REIA Name],\n\nI had to share this with you - I found Clumse and it's absolutely incredible! The AI assistant is so good that my tenants think they're talking to a real person. It handles everything from answering questions to collecting rent, and I actually have time to focus on growing my business.\n\nI really think our REIA members would love this. It's been such a game changer for me and I know it would help others too.\n\nBest regards,\n[Your Name]`,
    `Subject: This is going to change everything for our members\n\nHi [Local REIA Name],\n\nI discovered Clumse and I'm so excited about it that I had to tell you! It's completely transformed how I manage my rentals. The AI assistant is brilliant - it answers tenant questions instantly, handles rent collection automatically, and gives me these amazing insights about my properties.\n\nI really think our REIA members would benefit from knowing about this. It's honestly the best tool I've found for property management.\n\nThanks for all you do!\n[Your Name]`,
  ];

  const shuffleTemplates = (arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const handleOpenTemplateModal = () => {
    const shuffled = shuffleTemplates(EMAIL_TEMPLATES);
    setShuffledTemplates(shuffled);
    setCurrentTemplateIndex(0);
    setIsTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => setIsTemplateModalOpen(false);

  const handleNextTemplate = () => {
    setCurrentTemplateIndex((prev) => (prev + 1) % shuffledTemplates.length);
  };

  const handlePrevTemplate = () => {
    setCurrentTemplateIndex(
      (prev) => (prev - 1 + shuffledTemplates.length) % shuffledTemplates.length
    );
  };

  const handleCopyCurrentTemplate = () => {
    const current = shuffledTemplates[currentTemplateIndex] || "";
    copyToClipboard(current);
  };

  // Reusable partner offer card
  const PartnerOfferCard = ({
    title,
    tagline,
    logoSrc,
    description,
    benefits = [],
    onContact,
  }) => (
    <PartnerSpotlight
      onClick={onContact}
      role="button"
      tabIndex={0}
      aria-label={`Contact ${title}`}
    >
      <CardGrid>
        <CardLeft>
          {logoSrc ? (
            <LogoImage src={logoSrc} alt={`${title} logo`} />
          ) : (
            <LogoPlaceholder>Logo</LogoPlaceholder>
          )}
        </CardLeft>
        <CardCenter>
          <PartnerHeader>{title}</PartnerHeader>
          {tagline ? <PartnerTagline>{tagline}</PartnerTagline> : null}
          {description ? (
            <PartnerDescription>{description}</PartnerDescription>
          ) : null}
          {benefits?.length ? (
            <BulletRow>
              {benefits.slice(0, 4).map((b, i) => (
                <span key={i}>• {b}</span>
              ))}
            </BulletRow>
          ) : null}
        </CardCenter>
      </CardGrid>
    </PartnerSpotlight>
  );

  return (
    <Container className="offers-container">
      <PageHeader
        icon={UserAddOutlined}
        title="Invite a Friend"
        subtitle="Share Clumse and earn free months when your friends sign up"
      />
      <InviteWrapper>
        <InviteStatsCard />
      </InviteWrapper>

      {/* REIA Email Promotion Section */}
      {/* REIA Promotion Section - Temporarily commented out - may be brought back later
      <REIAPromotionSection>
        <REIAHeader>
          <REIAIcon>
            <MailOutlined style={{ filter: 'brightness(0) invert(1)' }} />
          </REIAIcon>
          <REIATitle>Earn Up to 2 Free Months</REIATitle>
          <REIASubtitle>Share Clumse with your local REIA and get rewarded</REIASubtitle>
        </REIAHeader>

        <REIAContent>
          <REIADescription>
            Love Clumse? Help us spread the word to your <strong>local Real Estate Investment Association (REIA)</strong> 
            and earn <strong>up to 2 free months</strong> of service! Send an email to your local REIA about 
            how Clumse has helped your rental business, and earn even more if they respond or book a call with us.
          </REIADescription>

          <REIABenefits>
            <BenefitItem>
              <CheckCircleOutlined />
              <span>1 free month for sending the email</span>
            </BenefitItem>
            <BenefitItem>
              <CheckCircleOutlined />
              <span>+1 additional free month if your REIA replies or books a call</span>
            </BenefitItem>
            <BenefitItem>
              <CheckCircleOutlined />
              <span>Help fellow investors discover amazing tools</span>
            </BenefitItem>
            <BenefitItem>
              <CheckCircleOutlined />
              <span>Build relationships with your REIA community</span>
            </BenefitItem>
          </REIABenefits>

          <REIAInstructions>
            <InstructionsTitle>How to Participate:</InstructionsTitle>
            <InstructionsList>
              <InstructionStep>
                <StepNumber>1</StepNumber>
                <StepText>Use our thought-starter templates as inspiration and personalize them for your REIA</StepText>
              </InstructionStep>
              <InstructionStep>
                <StepNumber>2</StepNumber>
                <StepText>Send it to your <strong>local REIA</strong> with <strong>contact@clumse.com</strong> in BCC</StepText>
              </InstructionStep>
              <InstructionStep>
                <StepNumber>3</StepNumber>
                <StepText>We'll automatically add 1 free month to your account for sending the email</StepText>
              </InstructionStep>
              <InstructionStep>
                <StepNumber>4</StepNumber>
                <StepText>If your REIA replies or books a call with us, you'll get an additional free month!</StepText>
              </InstructionStep>
            </InstructionsList>
          </REIAInstructions>

          <REIAActions>
            <ActionCard>
              <ActionHeader>
                <ActionIcon>
                  <CopyOutlined />
                </ActionIcon>
                <ActionTitle>Copy Email Template</ActionTitle>
              </ActionHeader>
              <ActionDescription>
                Thought-starter templates you can personalize
              </ActionDescription>
              <ActionButton onClick={handleOpenTemplateModal}>
                Copy Template
                <ArrowRightOutlined style={{ filter: 'brightness(0) invert(1)' }} />
              </ActionButton>
            </ActionCard>

            <ActionCard>
              <ActionHeader>
                <ActionIcon>
                  <MailOutlined />
                </ActionIcon>
                <ActionTitle>Copy BCC Email</ActionTitle>
              </ActionHeader>
              <ActionDescription>
                contact@clumse.com - Add this to BCC field
              </ActionDescription>
              <ActionButton onClick={handleCopyEmail}>
                Copy Email
                <ArrowRightOutlined style={{ filter: 'brightness(0) invert(1)' }} />
              </ActionButton>
            </ActionCard>
          </REIAActions>

          <REIANote>
            <StarOutlined />
            <span>
              <strong>Note:</strong> Make sure to BCC contact@clumse.com so we can track your referral 
              and add the free months to your account. The first free month will be applied within 48 hours 
              of receiving your email. If your REIA replies or books a call, the additional free month will 
              be added automatically. <strong>Limit: Up to 2 free months per customer</strong> - you cannot 
              send to multiple REIAs to earn additional months.
            </span>
          </REIANote>
        </REIAContent>
      </REIAPromotionSection>
      */}

      {/* Template Modal - Temporarily commented out with REIA section
      <StyledModal
        open={isTemplateModalOpen}
        onCancel={handleCloseTemplateModal}
        footer={null}
        width={700}
        title={
          <ModalTitle>
            <MailOutlined style={{ filter: 'brightness(0) invert(1)' }} />
            Email Template
          </ModalTitle>
        }
        centered
      >
        <ModalContent>
          <TemplateCounter>
            Template {currentTemplateIndex + 1} of {shuffledTemplates.length}
          </TemplateCounter>
          
          <TemplateTextArea
            value={shuffledTemplates[currentTemplateIndex] || ''}
            readOnly
            autoSize={{ minRows: 12, maxRows: 20 }}
          />
          
          <ModalActions>
            <ArrowButton onClick={handlePrevTemplate} disabled={shuffledTemplates.length <= 1}>
              <ArrowLeftOutlined />
            </ArrowButton>
            <CopyButton onClick={handleCopyCurrentTemplate}>
              <CopyOutlined style={{ filter: 'brightness(0) invert(1)' }} />
              Copy to Clipboard
            </CopyButton>
            <ArrowButton onClick={handleNextTemplate} disabled={shuffledTemplates.length <= 1}>
              <ArrowRightOutlined />
            </ArrowButton>
          </ModalActions>
          
          <ModalHint>
            <BulbOutlined />
            These are thought-starter templates to get you going. You can and should personalize them to make them your own when emailing your REIA.
          </ModalHint>
        </ModalContent>
      </StyledModal>
      */}
    </Container>
  );
};

export default Offers;

// Enhanced Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    width: 90vw;
    max-width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  }

  &.offers-container {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  }
`;

// Partner spotlight block (placeholder for partner content)
const PartnerSpotlight = styled.div`
  width: 100%;
  max-width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto 1.5rem;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.08);
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover {
    box-shadow: 0 18px 38px rgba(0, 0, 0, 0.1);
  }
  &:active {
    transform: translateY(1px);
  }

  @media ${device.mobile} {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
    padding: 1rem;
    box-sizing: border-box;
    border-radius: 16px;
  }
`;

const PartnerHeader = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 800;
  text-align: left;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;

const PartnerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 20% 1fr; /* logo takes ~1/5 of width, rest is content */
  align-items: center;
  gap: 1rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const CardLeft = styled.div`
  display: flex;
  justify-content: center;
`;

const CardCenter = styled.div`
  text-align: left;

  @media ${device.mobile} {
    width: 100%;
  }
`;

const CardRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: row;
  }
`;

const BulletRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 1rem;
  color: rgba(0, 0, 0, 0.8);
  font-size: 0.78rem;
  font-family: var(--font-secondary);
  margin-top: 0.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    font-size: 0.8rem;
  }
`;

const CtaHint = styled.div`
  font-size: 0.82rem;
  color: rgba(0, 0, 0, 0.55);
  text-align: center;
`;

const LogoPlaceholder = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 8px;
  border: 2px dashed rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 600;
  background: #fafafa;
  flex-shrink: 0;
`;

const LogoImage = styled.img`
  width: 100%;
  max-width: 200px;
  aspect-ratio: 1 / 1;
  object-fit: contain;
  border-radius: 10px;
  background: transparent;
  border: none;
  box-shadow: none;

  @media ${device.mobile} {
    max-width: 120px;
    border-radius: 8px;
  }
`;

const InfoPlaceholder = styled.div`
  flex: 1;
  min-height: 48px;
  border-radius: 8px;
  border: 2px dashed rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  padding: 0.75rem 0.875rem;
  color: rgba(0, 0, 0, 0.55);
  background: #fcfcfc;
`;

const InfoBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PartnerTagline = styled.div`
  margin: 0.25rem 0 0.75rem 0;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 600;
  text-align: left;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 0.82rem;
    margin-bottom: 0.5rem;
    line-height: 1.4;
  }
`;

const PartnerDescription = styled.p`
  margin: 0 0 0.75rem 0;
  color: rgba(0, 0, 0, 0.78);
  line-height: 1.55;
  font-size: 0.9rem;
  text-align: left;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 0.85rem;
    line-height: 1.5;
  }
`;

const PartnerBenefits = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: rgba(0, 0, 0, 0.8);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 1rem;
  list-style: disc inside;
`;

const PartnerActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PartnerButton = styled.button`
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(44, 95, 45, 0.2);
`;

const PartnerSecondary = styled.button`
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  color: rgba(0, 0, 0, 0.75);
  font-weight: 600;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.85);
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const BulletList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 0.95rem;
    color: rgba(0, 0, 0, 0.75);
    line-height: 1.6;
  }

  .icon {
    color: var(--color-primary);
    margin-top: 0.25rem;
    flex-shrink: 0;
    font-size: 1.1rem;
  }
`;

const Disclosure = styled.div`
  background: rgba(14, 165, 233, 0.08);
  border-left: 4px solid #0ea5e9;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 0;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  line-height: 1.6;

  .icon {
    color: #0ea5e9;
    margin-top: 0.25rem;
    flex-shrink: 0;
    font-size: 1.1rem;
  }
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0;
  padding: 1.25rem;
  background: rgba(76, 175, 80, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(76, 175, 80, 0.15);

  .ant-checkbox-wrapper {
    flex: 1;
    font-size: 0.95rem;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.75);
  }
`;

const TitleIcon = styled.span`
  color: var(--color-primary);
  background: rgba(76, 175, 80, 0.12);
  border-radius: 10px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
`;

const VendorName = styled.span`
  color: var(--color-primary);
  font-weight: 600;
`;

const IntroText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.75);
  line-height: 1.6;
`;

const SectionDivider = styled.div`
  text-align: center;
  margin: 3rem 0 2rem;
  padding: 2rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
`;

const InviteWrapper = styled.div`
  margin-bottom: 3rem;
`;

// REIA Promotion Section Styled Components
const REIAPromotionSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }
`;

const REIAHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const REIAIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem auto;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.2);

  .anticon {
    font-size: 2rem;
    color: white;
  }
`;

const REIATitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const REIASubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 500;
`;

const REIAContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const REIADescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: var(--color-text);
  line-height: 1.6;
  text-align: center;
  margin-bottom: 3rem;
  font-weight: 500;
`;

const REIABenefits = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-text);
  line-height: 1.5;
  padding: 1.25rem;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.05) 0%,
    rgba(16, 185, 129, 0.05) 100%
  );
  border: 1px solid rgba(44, 95, 45, 0.15);
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(44, 95, 45, 0.1) 0%,
      rgba(16, 185, 129, 0.1) 100%
    );
    border-color: rgba(44, 95, 45, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.1);
  }

  .anticon {
    color: var(--color-primary);
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  span {
    font-weight: 500;
  }
`;

const REIAInstructions = styled.div`
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.05) 0%,
    rgba(16, 185, 129, 0.05) 100%
  );
  border-radius: 16px;
  padding: 2.5rem;
  margin-bottom: 3rem;
  border: 1px solid rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const InstructionsTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 1.5rem 0;
  text-align: center;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin: 0 0 1rem 0;
  }
`;

const InstructionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media ${device.mobile} {
    gap: 1rem;
  }
`;

const InstructionStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(44, 95, 45, 0.15);
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(44, 95, 45, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.1);
  }

  @media ${device.mobile} {
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 10px;
  }
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  background: var(--gradient-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;

  @media ${device.mobile} {
    width: 28px;
    height: 28px;
    font-size: 0.85rem;
  }
`;

const StepText = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-text);
  margin: 0;
  line-height: 1.5;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.45;
  }
`;

const REIAActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ActionCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  border: 2px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
    border-color: var(--color-primary);
  }
`;

const ActionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
`;

const ActionIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  .anticon {
    font-size: 1.5rem;
    color: white;
    filter: brightness(0) invert(1) !important;
  }
`;

const ActionTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.5rem 0;
`;

const ActionDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-primary);
  color: white !important;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: white !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: translateY(0);
    color: white !important;
  }

  .anticon {
    font-size: 0.9rem;
    transition: transform 0.3s ease;
    color: white !important;
  }

  &:hover .anticon {
    color: white !important;
  }

  &:hover .anticon:last-child {
    transform: translateX(4px);
    color: white !important;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    padding: 0;
  }

  .ant-modal-header {
    background: var(--gradient-primary);
    border-bottom: none;
    padding: 1.5rem 2rem;

    .ant-modal-title {
      color: white;
      font-family: var(--font-primary);
      font-weight: 700;
      font-size: 1.25rem;
    }
  }

  .ant-modal-close {
    color: white;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .ant-modal-body {
    padding: 0;
  }

  @media ${device.mobile} {
    .ant-modal {
      margin: 1rem;
      max-width: calc(100vw - 2rem);
    }

    .ant-modal-content {
      border-radius: 16px;
    }

    .ant-modal-header {
      padding: 1rem 1.5rem;

      .ant-modal-title {
        font-size: 1.1rem;
      }
    }
  }
`;

// Removed duplicate ModalTitle (defined earlier)

const ModalContent = styled.div`
  padding: 0 1.5rem;

  @media ${device.mobile} {
    padding: 0 1rem;
  }
`;

const TemplateCounter = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  text-align: center;
  margin: 1.5rem 0 1rem 0;
  font-weight: 500;
`;

const ArrowButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--color-border-light);
  background: white;
  color: var(--color-text);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: var(--color-primary-glow);
    color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(44, 95, 45, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .anticon {
    font-size: 1rem;
  }
`;

const TemplateTextArea = styled(Input.TextArea)`
  font-family: var(--font-secondary) !important;
  font-size: 0.95rem !important;
  line-height: 1.6 !important;
  border: 2px solid var(--color-border-light) !important;
  border-radius: 12px !important;
  padding: 1.25rem !important;
  background: #fafbfc !important;
  margin: 0 !important;

  &:focus {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px var(--color-primary-glow) !important;
  }

  @media ${device.mobile} {
    font-size: 0.9rem !important;
    padding: 1rem !important;
  }
`;

const ModalActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;

  @media ${device.mobile} {
    margin: 1rem 0;
    gap: 0.75rem;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  .anticon {
    font-size: 0.9rem;
    color: white !important;
  }
`;

const ModalHint = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  text-align: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.05) 0%,
    rgba(16, 185, 129, 0.05) 100%
  );
  border: 1px solid rgba(44, 95, 45, 0.1);
  border-radius: 12px;
  margin: 0 0 1.5rem 0;

  .anticon {
    color: var(--color-primary);
    font-size: 1rem;
  }

  @media ${device.mobile} {
    font-size: 0.85rem;
    padding: 0.875rem 1rem;
    margin: 0 0 1rem 0;
  }
`;

const REIANote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.5rem;
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.1) 0%,
    rgba(255, 152, 0, 0.1) 100%
  );
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: var(--color-text);
  line-height: 1.5;

  .anticon {
    color: #ffc107;
    font-size: 1.1rem;
    margin-top: 0.1rem;
    flex-shrink: 0;
  }

  span {
    font-weight: 500;
  }
`;
